"use client";
import { KeyMetricsProps, KeyMetricStat } from "@/types/data";
import { gsap } from "@/lib/gsap";
import dynamic from "next/dynamic";
import { useEffect, useState, useRef } from "react";


const CountUp = dynamic(() => import("react-countup"), {
  ssr: false,
  loading: () => <span>0</span>,
});

export default function KeyMetrics({ keyMetrics }: KeyMetricsProps) {
  const [isClient, setIsClient] = useState(false);
  const [animateStats, setAnimateStats] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const statsContainerRef = useRef<HTMLDivElement>(null);
  const hasAnimatedFromTop = useRef(false);

  const { tagline, title, description, stats } = keyMetrics;

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    const element = ref.current;
    const headerElement = headerRef.current;
    const statsContainer = statsContainerRef.current;

    if (!element || !headerElement || !statsContainer) return;

    
    const ctx = gsap.context(() => {
      
      gsap.set(headerElement, {
        opacity: 1, 
        y: 0, 
      });

      gsap.set(statsContainer.children, {
        opacity: 1, 
        y: 0, 
        scale: 1, 
      });

      
      gsap.set(statsContainer, {
        opacity: 1,
        y: 0,
      });

      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          end: "top 20%",
          toggleActions: "play none none reverse",
          onEnter: () => {
            
            hasAnimatedFromTop.current = false;
          },
          onEnterBack: () => {
            
            
          },
          onLeave: () => {
            setAnimateStats(true);
            
            hasAnimatedFromTop.current = false;
          },
          onLeaveBack: () => {
            setAnimateStats(false);
          },
        },
      });

      
      tl.fromTo(
        headerElement,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
        }
      ).fromTo(
        statsContainer.children,
        { opacity: 0, y: 40, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.15,
          ease: "power2.out",
          onStart: () => {
            
            setAnimateStats(true);
            setAnimationKey((prev) => prev + 1);
          },
        },
        "-=0.4"
      );
    }, [headerRef, statsContainerRef]);

    return () => {
      ctx.revert();
    };
  }, [isClient]);

  return (
    <div ref={ref} className="text-center lg:pb-16  ">
      <div ref={headerRef}>
        {tagline && (
          <div className="flex items-center justify-center mb-4">
            <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
            <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
              {tagline}
            </p>
          </div>
        )}

        {title && (
          <h2 className="text-4xl font-bold mb-8 text-theme-secondary">
            {title}
          </h2>
        )}

        {description && (
          <p className="mt-4 text-lg text-theme-secondary-light md:max-w-[546px] mx-auto">
            {description}
          </p>
        )}
      </div>

      <div
        ref={statsContainerRef}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-8 max-w-5xl mx-auto mt-8"
      >
        {stats &&
          stats.length > 0 &&
          stats.map((metric: KeyMetricStat, index: number) => (
            <div key={index} className="p-2 lg:p-6">
              <div className="text-4xl font-bold text-theme-primary">
                {isClient && animateStats ? (
                  <CountUp
                    key={`${animationKey}-${index}`}
                    start={0}
                    end={Number(metric.value)}
                    duration={3}
                    delay={index * 0.1}
                    separator=","
                    suffix={metric.suffix || ""}
                  />
                ) : (
                  <span>0{metric.suffix || ""}</span>
                )}
              </div>
              <p className="mt-2 text-[#4B5563] font-semibold text-[17.156px] leading-[30.6px]">
                {metric.label}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
}
